/**
 * Storage Actions - Shared helpers (Hono)
 */

import type { Context } from 'hono'
import type { ModuleContext } from '@gzl10/nexus-sdk'

/**
 * Checks if a user has access to a file based on its visibility level.
 * Hono-native: reads `user` and `ability` from the request context.
 */
export function checkFileAccess(
  file: { visibility: string; created_by?: string | null },
  c: Context<any>,
  ctx: ModuleContext
): void {
  if (file.visibility === 'public') return

  const user = c.get('user') as { id: string } | undefined
  if (!user) throw new ctx.core.errors.UnauthorizedError('AUTH_REQUIRED')

  if (file.visibility === 'private') {
    // Private = owner + ADMIN only. CASL check first (ADMIN has 'manage all').
    const ability = c.get('ability')
    const { subject, ForbiddenError: CASLForbiddenError } = ctx.core.abilities
    try {
      CASLForbiddenError.from(ability).throwUnlessCan(
        'manage',
        subject('StorageFile', file as unknown as Record<string, unknown>)
      )
    } catch {
      // Not ADMIN — check ownership explicitly
      if (file.created_by !== user.id) {
        throw new ctx.core.errors.ForbiddenError('PERMISSION_DENIED')
      }
    }
  }
  // internal: authenticated is enough, no further CASL check
}

/**
 * Extracts upload options from already-parsed input (form fields).
 */
export function extractUploadOptions(input: Record<string, unknown>) {
  const folder = typeof input['folder'] === 'string' ? input['folder'] as string : undefined
  const scope = typeof input['scope'] === 'string' ? input['scope'] as string : undefined
  const dedupeRaw = input['dedupe']
  const dedupe = dedupeRaw === 'true' || dedupeRaw === true
  const visibilityRaw = input['visibility']
  const visibility = ((typeof visibilityRaw === 'string' ? visibilityRaw : undefined) ?? 'internal') as 'public' | 'internal' | 'private'
  const userId = typeof input['_authUserId'] === 'string' ? input['_authUserId'] as string : undefined

  return { folder, scope, dedupe, visibility, userId }
}
